import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { IntelligenceDashboard, DashboardData, Manuscript } from "../lib/types.js";
import { Icon, type IconName } from "../components/Icon.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  manuscript: Manuscript;
  reloadKey?: number;
  onOpenTab: (tab: string) => void;
}

type Tone = "purple" | "teal" | "amber" | "neutral";

function sceneBucket(status: string | null): "draft" | "revised" | "review" | "issue" {
  const l = (status ?? "").toLowerCase();
  if (l.includes("continu") || l.includes("issue") || l.includes("conflict")) return "issue";
  if (l.includes("review") || l.includes("needs")) return "review";
  if (l.includes("revis") || l.includes("final") || l.includes("done") || l.includes("complete")) return "revised";
  return "draft";
}

/** Story Intelligence at a glance — counts + analytics that let you understand
 * the book's shape in seconds, each card drilling into its section. */
export function IntelDashboard({ projectId, manuscript, reloadKey, onOpenTab }: Props) {
  const [data, setData] = useState<IntelligenceDashboard | null>(null);
  const [dash, setDash] = useState<DashboardData | null>(null);

  const load = useCallback(async () => {
    try { setData(await api.intelDashboard(projectId)); } catch { /* non-fatal */ }
    try { setDash(await api.dashboard(projectId)); } catch { /* non-fatal */ }
  }, [projectId]);
  useEffect(() => { void load(); }, [load, reloadKey]);

  // Chapter word counts (reading order) → word stats + pacing series.
  const chapterWords = useMemo(
    () => [...manuscript.chapters].sort((a, b) => a.position - b.position).map((c) => c.wordCount),
    [manuscript.chapters]
  );
  const wordStats = useMemo(() => {
    const nonEmpty = chapterWords.filter((w) => w > 0);
    const avg = chapterWords.length ? Math.round(chapterWords.reduce((s, w) => s + w, 0) / chapterWords.length) : 0;
    return { avg, longest: nonEmpty.length ? Math.max(...nonEmpty) : 0, shortest: nonEmpty.length ? Math.min(...nonEmpty) : 0 };
  }, [chapterWords]);

  const sceneStatus = useMemo(() => {
    const c = { draft: 0, revised: 0, review: 0, issue: 0 };
    for (const s of manuscript.scenes) c[sceneBucket(s.status)]++;
    return c;
  }, [manuscript.scenes]);
  const totalScenes = manuscript.scenes.length;

  if (!data) return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;

  const t = data.threads;
  const words = dash?.stats.wordCount ?? manuscript.totals.wordCount;
  const target = dash?.stats.targetWordCount ?? null;
  const pct = target ? Math.min(100, Math.round((words / target) * 100)) : null;

  return (
    <div className="intel-dash">
      <div className="intel-dash-head">
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <Icon name="layers" size={18} style={{ color: "var(--accent)" }} />
          <h3 style={{ margin: 0, fontSize: 16 }}>Manuscript at a Glance</h3>
        </div>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <select className="select intel-scope" defaultValue="all" title="Analysis scope">
            <option value="all">Entire Manuscript</option>
          </select>
          <span className="faint intel-dash-scenes">{formatNumber(data.scenes)} scenes analyzed</span>
        </div>
      </div>

      <div className="intel-cards">
        <Card icon="users" tone="purple" label="Characters" total={data.characters.total} onClick={() => onOpenTab("characters")}
          lines={[`${data.characters.main} main`, `${data.characters.supporting} supporting`, `${data.characters.minor} minor`]} />
        <Card icon="map" tone="teal" label="Locations" total={data.locations.total} onClick={() => onOpenTab("locations")}
          lines={[`${data.locations.primary} primary`, `${data.locations.sublocations} sublocations`]} />
        <Card icon="thread" tone="teal" label="Plot Threads" total={t.total} onClick={() => onOpenTab("plots")}
          lines={[`${t.active + t.developing} active`, `${t.resolved} resolved`, ...(t.possiblyForgotten ? [`${t.possiblyForgotten} may be forgotten`] : [])]}
          warn={t.possiblyForgotten > 0 || t.needsAttention > 0} />
        <Card icon="heart" tone="purple" label="Relationships" total={data.relationships} onClick={() => onOpenTab("relationships")}
          lines={["tracked pairs"]} />
        <Card icon="timeline" tone="teal" label="Timeline" total={data.timeline} onClick={() => onOpenTab("timeline")}
          lines={["events"]} />
        <Card icon="target" tone="amber" label="Story Promises" total={data.promises.total} onClick={() => onOpenTab("promises")}
          lines={[`${data.promises.open} open`, `${data.promises.paid} paid off`, ...(data.promises.forgotten ? [`${data.promises.forgotten} may be forgotten`] : [])]}
          warn={data.promises.forgotten > 0} />
        <Card icon="history" tone="neutral" label="Revision" total={data.revisionOpenTasks} onClick={() => onOpenTab("revision")}
          lines={[data.revisionOpenTasks ? "open tasks" : "no open tasks"]} />
        <Card icon="alert" tone="amber" label="Continuity" total={data.continuityOpen} onClick={() => onOpenTab("continuity")}
          lines={[data.continuityOpen ? "potential issues" : "no open issues"]} warn={data.continuityOpen > 0} />
        <Card icon="info" tone="teal" label="Needs Review" total={data.reviewOpen} onClick={() => onOpenTab("review")}
          lines={[data.reviewOpen ? "awaiting your decision" : "all clear"]} danger={data.reviewOpen > 0} />
      </div>

      <div className="intel-analytics">
        {/* Word Count Progress */}
        <section className="card intel-analytic">
          <div className="intel-analytic-head">
            <div className="row gap-2"><Icon name="layers" size={15} style={{ color: "var(--accent)" }} /><h4>Word Count Progress</h4></div>
            <button className="btn btn-sm btn-ghost" onClick={() => onOpenTab("timeline")}>View Timeline <Icon name="chevronRight" size={13} /></button>
          </div>
          <div className="intel-wc-top">
            <span className="intel-wc-num">{formatNumber(words)}{target ? <span className="faint"> / {formatNumber(target)}</span> : null}</span>
            {pct != null && <span className="intel-wc-pct">{pct}%</span>}
          </div>
          {pct != null ? (
            <div className="intel-wc-bar"><div style={{ width: `${pct}%` }} /></div>
          ) : (
            <p className="faint" style={{ fontSize: 11.5, margin: "4px 0 0" }}>Set a target word count in Settings to track progress.</p>
          )}
          <div className="intel-wc-stats">
            <Stat label="Average per chapter" value={formatNumber(wordStats.avg)} />
            <Stat label="Longest chapter" value={formatNumber(wordStats.longest)} />
            <Stat label="Shortest chapter" value={formatNumber(wordStats.shortest)} />
          </div>
        </section>

        {/* Pacing (chapter length) */}
        <section className="card intel-analytic">
          <div className="intel-analytic-head">
            <div className="row gap-2"><Icon name="history" size={15} style={{ color: "var(--accent)" }} /><h4>Pacing Analysis</h4></div>
            <button className="btn btn-sm btn-ghost" onClick={() => onOpenTab("pacing")}>View Details <Icon name="chevronRight" size={13} /></button>
          </div>
          <PacingChart values={chapterWords} />
          <div className="intel-pacing-legend">
            <span><span className="dot" style={{ background: "var(--accent)" }} /> Chapter length</span>
            <span><span className="dot" style={{ background: "color-mix(in srgb, var(--text-faint) 50%, transparent)" }} /> Average</span>
          </div>
        </section>

        {/* Scene Status */}
        <section className="card intel-analytic">
          <div className="intel-analytic-head">
            <div className="row gap-2"><Icon name="file" size={15} style={{ color: "var(--accent)" }} /><h4>Scene Status</h4></div>
            <button className="btn btn-sm btn-ghost" onClick={() => onOpenTab("summaries")}>View All Scenes <Icon name="chevronRight" size={13} /></button>
          </div>
          <div className="intel-scene-row">
            <SceneRing counts={sceneStatus} total={totalScenes} />
            <div className="intel-scene-legend">
              <StatusRow color="var(--accent)" label="Draft" n={sceneStatus.draft} total={totalScenes} />
              <StatusRow color="#a78bfa" label="Revised" n={sceneStatus.revised} total={totalScenes} />
              <StatusRow color="#5b8def" label="Needs Review" n={sceneStatus.review} total={totalScenes} />
              <StatusRow color="#f59e0b" label="Continuity Issues" n={sceneStatus.issue} total={totalScenes} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function Card({ icon, label, total, lines, onClick, warn, danger, tone }: {
  icon: IconName; label: string; total: number; lines: string[]; onClick: () => void; warn?: boolean; danger?: boolean; tone: Tone;
}) {
  return (
    <button className={`intel-card tone-${tone} ${warn ? "is-warn" : ""} ${danger ? "is-danger" : ""}`} onClick={onClick}>
      <div className="intel-card-top">
        <span className="intel-card-chip"><Icon name={icon} size={14} /></span>
        <span className="intel-card-label">{label}</span>
        <Icon name="chevronRight" size={13} className="intel-card-arrow" />
      </div>
      <div className="intel-card-total">{formatNumber(total)}</div>
      <div className="intel-card-lines">{lines.map((l, i) => <span key={i}>{l}</span>)}</div>
    </button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="intel-stat">
      <div className="intel-stat-label faint">{label}</div>
      <div className="intel-stat-value">{value}</div>
    </div>
  );
}

function StatusRow({ color, label, n, total }: { color: string; label: string; n: number; total: number }) {
  const p = total ? Math.round((n / total) * 100) : 0;
  return (
    <div className="intel-status-row">
      <span className="dot" style={{ background: color }} />
      <span className="intel-status-label">{label}</span>
      <span className="intel-status-n">{n}</span>
      <span className="intel-status-pct faint">{p}%</span>
    </div>
  );
}

function SceneRing({ counts, total }: { counts: { draft: number; revised: number; review: number; issue: number }; total: number }) {
  const t = Math.max(1, total);
  const s1 = (counts.draft / t) * 100;
  const s2 = s1 + (counts.revised / t) * 100;
  const s3 = s2 + (counts.review / t) * 100;
  const bg = `conic-gradient(var(--accent) 0 ${s1}%, #a78bfa ${s1}% ${s2}%, #5b8def ${s2}% ${s3}%, #f59e0b ${s3}% 100%)`;
  return (
    <div className="intel-ring" style={{ background: total ? bg : "color-mix(in srgb, var(--text-faint) 25%, transparent)" }}>
      <div className="intel-ring-hole">
        <div className="intel-ring-num">{total}</div>
        <div className="intel-ring-label">Scenes</div>
      </div>
    </div>
  );
}

/** A clean area+line of per-chapter length, with a muted average band. */
function PacingChart({ values }: { values: number[] }) {
  if (values.length < 2) return <div className="intel-pacing-empty faint">Not enough chapters to chart pacing yet.</div>;
  const W = 100, H = 40;
  const max = Math.max(...values, 1);
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - (v / max) * (H - 4) - 2;
    return [x, y] as const;
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const area = `${line} L${W},${H} L0,${H} Z`;
  const avgY = H - (avg / max) * (H - 4) - 2;
  return (
    <svg className="intel-pacing-svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="pacefill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="color-mix(in srgb, var(--accent) 45%, transparent)" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <line x1="0" y1={avgY} x2={W} y2={avgY} stroke="color-mix(in srgb, var(--text-faint) 45%, transparent)" strokeWidth="0.5" strokeDasharray="2 2" />
      <path d={area} fill="url(#pacefill)" />
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="1" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="1.1" fill="#a78bfa" vectorEffect="non-scaling-stroke">
          <title>Chapter {i + 1}: {formatNumber(values[i]!)} words</title>
        </circle>
      ))}
    </svg>
  );
}
